// React
import React, { useEffect, useRef, useState } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faLineColumns, faSearch, faSquare } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { Button } from "../buttons/button";
import { DropdownAlignment, DropdownDirection, DropdownPanel, DropdownPosition, useDropdownPanel } from "../dropdown-panel/dropdown-panel";
import { FormCheckbox } from "../forms/form-checkbox";
import { getDataTestId } from "../functions/data-test-ids";
import { InputString } from "../inputs/input-string";

// CSS
import "./select-table-columns-dropdown.css";

const SELECT_COLUMNS_SEARCH_THRESHOLD = 10;

// Sentinel written to SelectedColumns when the user picks "Select none". The
// library's column-filter helpers treat an empty array as "no filter → show
// all", so a non-empty array containing only a non-matching field is the
// cleanest way to express "show none" without changing library semantics.
const SELECT_COLUMNS_NONE_SENTINEL = "__eone_ui_select_columns_none__";

function SelectTableColumnsDropdown(props) {
    const {
        open,
        close,
        toggleOpen,
        triggerRef,
        style
    } = useDropdownPanel(
        DropdownPosition.Below,
        DropdownDirection.Down,
        DropdownAlignment.AlignRight,
        1,
        window.innerHeight * 0.5
    );

    const dropdownPanelRef = useRef(null);
    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        if (!open) {
            setSearchValue("");
        }
    }, [open]);

    useEffect(() => {
        setSearchValue("");
    }, [props.Columns]);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            const outsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
            const outsidePanel = dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target);
            if (outsideTrigger && outsidePanel) {
                close();
            }
        };
        window.addEventListener("mousedown", handleClickOutside);
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open, close]);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            close();
        }
    };

    if (props.Visible === false) {
        return null;
    }

    const visibleColumns = (props.Columns || []).filter(column =>
        (column.Visible === undefined || column.Visible)
        && typeof column.Label === "string"
        && column.Label.length > 0
    );
    const rawSelectedColumns = Array.isArray(props.SelectedColumns) ? props.SelectedColumns : [];
    const selectedColumns = rawSelectedColumns.filter(fieldName => fieldName !== SELECT_COLUMNS_NONE_SENTINEL);
    const showSearch = visibleColumns.length > SELECT_COLUMNS_SEARCH_THRESHOLD;

    const toggleColumn = (fieldName) => {
        const column = visibleColumns.find(c => c.FieldName === fieldName);
        if (column?.Locked) return;
        const next = selectedColumns.includes(fieldName)
            ? selectedColumns.filter(f => f !== fieldName)
            : [...selectedColumns, fieldName];
        // Unchecking the last checked column must not emit [] - the column-filter helpers read an empty
        // array as "no filter → show all", so it would re-show every column while leaving every checkbox
        // unchecked. Fall through to the same sentinel "Select none" writes.
        props.OnSelectedColumnsChange?.(next.length > 0 ? next : [SELECT_COLUMNS_NONE_SENTINEL]);
    };

    const selectAll = () => {
        props.OnSelectedColumnsChange?.(visibleColumns.map(column => column.FieldName));
    };

    const selectNone = () => {
        props.OnSelectedColumnsChange?.([SELECT_COLUMNS_NONE_SENTINEL]);
    };

    const changeSearch = (value) => {
        setSearchValue(value || "");
    };

    const getFilteredColumns = () => {
        if (!searchValue) return visibleColumns;
        const needle = searchValue.toLowerCase();
        return visibleColumns.filter(column => (column.Label || "").toLowerCase().includes(needle));
    };

    return (
        <span ref={triggerRef} className="eone-ui-select-table-columns-dropdown-container" onKeyDown={handleKeyDown}>
            <Button
                Class={props.Class || "eone-ui-button-container-tertiary"}
                Label="Select columns"
                HideLabel={props.HideLabel}
                Icon={faLineColumns}
                DataTestId={props.DataTestId}
                OnClick={toggleOpen} />
            <DropdownPanel Style={style} Open={open} Close={close} DropdownPanelRef={dropdownPanelRef}>
                <SelectTableColumnsDropdownMenu
                    Open={open}
                    Columns={getFilteredColumns()}
                    HasColumns={visibleColumns.length > 0}
                    SelectedColumns={selectedColumns}
                    ShowSearch={showSearch}
                    SearchValue={searchValue}
                    OnSearchChange={changeSearch}
                    OnToggle={toggleColumn}
                    OnSelectAll={selectAll}
                    OnSelectNone={selectNone}
                    OnClose={close}
                    DataTestId={props.DataTestId} />
            </DropdownPanel>
        </span>
    );
}

function SelectTableColumnsDropdownMenu(props) {
    if (!props.Open) {
        return null;
    }

    const getSearch = () => {
        if (!props.ShowSearch) return null;
        return (
            <div className="eone-ui-select-table-columns-dropdown-menu-search">
                <InputString
                    Value={props.SearchValue}
                    OnChange={props.OnSearchChange}
                    Placeholder="Search"
                    Icon={faSearch}
                    AutoFocus
                    DataTestId={getDataTestId(props.DataTestId, "search")} />
            </div>
        );
    };

    const getItems = () => {
        if (!props.HasColumns) {
            return (
                <div className="eone-ui-select-table-columns-dropdown-menu-empty">No columns available</div>
            );
        }
        if (props.Columns.length === 0) {
            return (
                <div className="eone-ui-select-table-columns-dropdown-menu-empty">No matching columns</div>
            );
        }
        return props.Columns.map(column => (
            <div key={column.FieldName} className="eone-ui-select-table-columns-dropdown-menu-item">
                <FormCheckbox
                    Label={column.Label}
                    Data={column.FieldName}
                    Value={column.Locked ? true : props.SelectedColumns.includes(column.FieldName)}
                    ReadOnly={column.Locked}
                    OnChange={props.OnToggle}
                    DataTestId={getDataTestId(props.DataTestId, column.FieldName)} />
            </div>
        ));
    };

    return (
        <div className="eone-ui-select-table-columns-dropdown-menu">
            {getSearch()}
            <div className="eone-ui-select-table-columns-dropdown-menu-actions">
                <button
                    type="button"
                    className="eone-ui-select-table-columns-dropdown-menu-action"
                    onClick={props.OnSelectAll}
                    data-testid={getDataTestId(props.DataTestId, "select-all")}>
                    <FontAwesomeIcon icon={faCheckSquare} fixedWidth />
                    <span>Select all</span>
                </button>
                <button
                    type="button"
                    className="eone-ui-select-table-columns-dropdown-menu-action"
                    onClick={props.OnSelectNone}
                    data-testid={getDataTestId(props.DataTestId, "select-none")}>
                    <FontAwesomeIcon icon={faSquare} fixedWidth />
                    <span>Select none</span>
                </button>
            </div>
            <div className="eone-ui-select-table-columns-dropdown-menu-divider" />
            <div className="eone-ui-select-table-columns-dropdown-menu-items">
                {getItems()}
            </div>
        </div>
    );
}

export { SELECT_COLUMNS_NONE_SENTINEL, SelectTableColumnsDropdown };
